import { CircularProgress } from '@civilgrid/ui/components';
import { useFeatureFlagEnabled } from '@posthog/react';
import { Outlet } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { handleForbiddenResource } from '~/common/authUtils';
import rootStore from '~/store';
import LeftNav from '../common/LeftNav';
import LeftNavSection from '../common/LeftNav/LeftNavSection';
import NavItem from '../common/LeftNav/NavItem';
import ProtectedRouteLayout from '../common/ProtectedRouteLayout';

export default function AdminLayout() {
  const { me, isUserBootstrapped } = useSnapshot(rootStore);
  const sswegEnabled = useFeatureFlagEnabled('ssweg-enabled');
  const subscriptionsDashboardEnabled = useFeatureFlagEnabled('subscription-enable-status-dashboard');

  useEffect(() => {
    if (isUserBootstrapped && !me?.isAdmin) {
      handleForbiddenResource();
    }
  }, [isUserBootstrapped, me?.isAdmin]);

  if (!isUserBootstrapped || !me?.isAdmin) return <CircularProgress indeterminate />;

  return (
    <ProtectedRouteLayout>
      <div className="flex h-full w-full grow flex-row">
        <LeftNav aria-label="Admin">
          <LeftNavSection title="Projects">
            <NavItem to="/admin/transactions">Transactions</NavItem>
            <NavItem to="/admin/move-projects">Move Project</NavItem>
          </LeftNavSection>

          <LeftNavSection title="Organizations">
            {subscriptionsDashboardEnabled && (
              <NavItem to="/admin/subscriptions" data-testid="admin-nav-subscriptions">
                Subscriptions
              </NavItem>
            )}
            <NavItem to="/admin/create-org">Create Organization</NavItem>
            <NavItem to="/admin/create-sandbox">Create Sandbox Org</NavItem>
            <NavItem to="/admin/manage-project-billing">Manage Project Billing</NavItem>
            <NavItem to="/admin/update-organization-subscription">Update Organization Subscription</NavItem>
            <NavItem to="/admin/update-linked-organizations">Linked Organizations</NavItem>
            <NavItem to="/admin/join-org-as-admin">Join Org</NavItem>
            <NavItem to="/admin/test-env-override">Test Environment Override</NavItem>
          </LeftNavSection>

          <LeftNavSection title="Users">
            <NavItem to="/admin/unapproved-users">Unapproved Users</NavItem>
          </LeftNavSection>

          <LeftNavSection title="CivilSearch">
            <NavItem to="/admin/civil-search/searchers">Civil Searchers</NavItem>
            <NavItem to="/admin/civil-search/requests">Data Requests</NavItem>
            <NavItem to="/admin/civil-search/agencies">Agencies</NavItem>
            <NavItem to="/admin/civil-search/projects">Projects</NavItem>
            {sswegEnabled && <NavItem to="/admin/civil-search/ssweg">SSWEG</NavItem>}
          </LeftNavSection>

          <EngineeringLinks />
        </LeftNav>
        <div className="h-full w-full overflow-auto">
          <div className="mx-3 has-[[data-fill-height]]:flex has-[[data-fill-height]]:h-full has-[[data-fill-height]]:flex-col">
            <Outlet />
          </div>
        </div>
      </div>
    </ProtectedRouteLayout>
  );
}

function EngineeringLinks() {
  const { me } = useSnapshot(rootStore) as typeof rootStore;
  const exportEscalationsEnabled = useFeatureFlagEnabled('export-enable-escalations');

  if (!me?.isSuperAdmin) {
    return null;
  }

  return (
    <LeftNavSection title="Engineering">
      <NavItem to="/admin/engineering-tools">Tools</NavItem>
      <NavItem to="/admin/org-special-datasets" data-testid="admin-nav-org-special-datasets">
        Org Special Datasets
      </NavItem>
      <NavItem to="/admin/layer-toggles" data-testid="admin-nav-layer-toggles">
        Layer Availability
      </NavItem>
      <NavItem to="/admin/extract-tools" data-testid="admin-nav-extract-tools">
        Extract Tools
      </NavItem>
      <NavItem to="/admin/pge-cloud-sync" data-testid="admin-nav-pge-cloud-sync">
        PG&E Cloud Sync
      </NavItem>
      {exportEscalationsEnabled && (
        <NavItem to="/admin/export-escalations" data-testid="admin-nav-export-escalations">
          Export Tools
        </NavItem>
      )}
      <NavItem to="/admin/reset-demo-org" data-testid="admin-nav-reset-demo-org">
        Reset Demo Org
      </NavItem>
    </LeftNavSection>
  );
}
